import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './LearningShell.module.css';

/**
 * LearningShell — the unified frame every learning surface routes through
 * (docs/ide-ux-spec.md §16.1). Provides one consistent header band
 * (breadcrumb · title · subtitle · actions) above a content area; the rails
 * (course outline left, Companion right) are mounted once in Layout and overlay
 * this. The "morphing center" is just whatever children a route renders here:
 * the course outline, an item's reader, or — in `fullBleed` mode — the lab IDE.
 *
 * `fullBleed` drops the centered max-width / padding so a surface (the editor)
 * can fill the viewport; otherwise the shell centers readable content.
 */
interface LearningShellProps {
  /** Header title. Required unless `bare` is set (which omits the header). */
  title?: string;
  breadcrumb?: { label: string; to: string };
  subtitle?: React.ReactNode;
  /** Right-aligned header actions (e.g. Run / Submit on the lab IDE). */
  actions?: React.ReactNode;
  /** Fill the viewport with no max-width/padding (for the editor surface). */
  fullBleed?: boolean;
  /**
   * Widen the centered content column (for media-heavy surfaces like the
   * lecture player, where an 860px column makes the video a postage stamp).
   * Still centered/padded — unlike `fullBleed`. Ignored when `fullBleed`.
   */
  wide?: boolean;
  /**
   * Render only the content area, skipping the header band entirely. Used by
   * the lab IDE, which supplies its own toolbar/status chrome and must not be
   * pushed down (or have its layout collide) by the shell's header.
   */
  bare?: boolean;
  children: React.ReactNode;
}

const LearningShell: React.FC<LearningShellProps> = ({
  title,
  breadcrumb,
  subtitle,
  actions,
  fullBleed = false,
  wide = false,
  bare = false,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={`${styles.shell} ${fullBleed ? styles.fullBleed : ''} ${
        wide && !fullBleed ? styles.wide : ''
      }`}
    >
      {!bare && (
        <header className={styles.header}>
          <div className={styles.headingGroup}>
            {breadcrumb && (
              <nav className={styles.breadcrumb} aria-label={t('course.breadcrumb_label')}>
                <Link to={breadcrumb.to}>{breadcrumb.label}</Link>
              </nav>
            )}
            <h1 className={styles.title}>{title}</h1>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
          </div>
          {actions && <div className={styles.actions}>{actions}</div>}
        </header>
      )}

      <div className={styles.content}>{children}</div>
    </div>
  );
};

export default LearningShell;
